import { onError } from "@apollo/client/link/error";
import { GRAPHQL_ERROR_CODES } from "@/lib/graphql/error-handler";
import { useAuthStore } from "@/lib/store/auth-enhanced";

/**
 * Enhanced auth error link with localized error handling
 * This replaces the basic auth-error-link.ts with internationalization support
 */
export const localizedAuthErrorLink = onError(({ graphQLErrors }) => {
  // Handle GraphQL errors
  if (graphQLErrors) {
    for (const error of graphQLErrors) {
      const { extensions } = error;
      const rawCode = extensions?.code;
      const errorCode = typeof rawCode === "string" ? rawCode : undefined;

      // Check if it's an authentication error using our standardized error codes
      const authErrorCodes: string[] = [
        GRAPHQL_ERROR_CODES.INVALID_CREDENTIALS,
        GRAPHQL_ERROR_CODES.TOKEN_EXPIRED,
        GRAPHQL_ERROR_CODES.TOKEN_INVALID,
        GRAPHQL_ERROR_CODES.USER_NOT_FOUND,
        GRAPHQL_ERROR_CODES.USER_DISABLED,
        GRAPHQL_ERROR_CODES.SESSION_EXPIRED,
        GRAPHQL_ERROR_CODES.UNAUTHORIZED,
      ];

      if (errorCode && authErrorCodes.includes(errorCode)) {
        // Get current auth state
        const authStore = useAuthStore.getState();

        const tokenErrorCodes: string[] = [
          GRAPHQL_ERROR_CODES.TOKEN_EXPIRED,
          GRAPHQL_ERROR_CODES.TOKEN_INVALID,
          GRAPHQL_ERROR_CODES.SESSION_EXPIRED,
          GRAPHQL_ERROR_CODES.UNAUTHORIZED,
        ];
        if (tokenErrorCodes.includes(errorCode)) {
          return;
        }

        // For credential errors, let the login form handle the error display
        if (errorCode === GRAPHQL_ERROR_CODES.INVALID_CREDENTIALS) {
          // Don't logout, let the login form show the localized error using useGraphQLErrorHandler
          return;
        }

        // For user-related errors (disabled, not found), logout
        const userErrorCodes: string[] = [
          GRAPHQL_ERROR_CODES.USER_NOT_FOUND,
          GRAPHQL_ERROR_CODES.USER_DISABLED,
        ];
        if (userErrorCodes.includes(errorCode)) {
          if (!authStore.isRefreshing) {
            // Simple logout message - components can show localized version
            authStore.logout(
              errorCode === GRAPHQL_ERROR_CODES.USER_DISABLED
                ? "Account disabled"
                : "User not found"
            );
          }
          return;
        }
      }
    }
  }

  // For other errors, let them pass through
});

/**
 * Error logging utility that respects user language preferences
 */
export const logLocalizedError = (error: Error | unknown, context?: string) => {
  // In development, log the full error
  if (import.meta.env.DEV) {
    console.error(`[GraphQL Error${context ? ` - ${context}` : ""}]:`, error);
  }

  // In production, you might want to send to a logging service
  // with the user's language context for better support
  if (import.meta.env.PROD) {
    // Example: Send to logging service with language context
    // loggingService.error(error, {
    //   context,
    //   userLanguage: i18n.language,
    //   timestamp: new Date().toISOString(),
    // });
  }
};
